import React, { useState, useEffect, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Checkbox,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import Tooltip from "@material-ui/core/Tooltip";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDialog-paper": {
      width: "90%",
      maxWidth: 1200,
      maxHeight: "90vh",
    },
  },
  tabPanel: {
    padding: theme.spacing(2),
  },
  messageField: {
    "& .MuiInputBase-root": {
      fontFamily: "monospace",
      fontSize: "0.9rem",
    },
  },
}));

const MaturationSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muito curto!").max(50, "Muito longo!").required("Obrigatório"),
  scheduledAt: Yup.string().nullable(), // Opcional - pode agendar ou iniciar manualmente
  endAt: Yup.string().nullable(), // Opcional
  businessStartTime: Yup.string().required("Obrigatório"),
  businessEndTime: Yup.string().required("Obrigatório"),
  messageInterval: Yup.number().min(1, "Mínimo 1 segundo").required("Obrigatório"),
});

const MaturationModal = ({ open, onClose, maturationId, onSave }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(0);
  const [whatsapps, setWhatsapps] = useState([]);
  const [contactPool, setContactPool] = useState([]); // Contatos disponíveis do pool
  const [selectedContactIds, setSelectedContactIds] = useState([]); // IDs dos contatos selecionados
  const [selectedWhatsappIds, setSelectedWhatsappIds] = useState([]); // IDs das conexões selecionadas
  const [attachment, setAttachment] = useState(null);
  const [attachment2, setAttachment2] = useState(null);
  const [attachment3, setAttachment3] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [deletingMedia, setDeletingMedia] = useState(null); // Para controlar qual mídia está sendo deletada

  const initialValues = {
    name: "",
    message1: "",
    message2: "",
    message3: "",
    message4: "",
    message5: "",
    whatsappId: "",
    whatsappIds: [],
    scheduledAt: "",
    endAt: "",
    businessStartTime: "09:00",
    businessEndTime: "18:00",
    businessDays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
    pauseOutsideHours: true,
    messageInterval: 20,
    contactIds: [],
    statusTicket: "closed",
    openTicket: "disabled",
  };

  useEffect(() => {
    if (open) {
      fetchWhatsapps();
      fetchContactPool();
      if (maturationId) {
        fetchMaturation();
      } else {
        setFormValues(null);
        setSelectedContactIds([]);
        setSelectedWhatsappIds([]);
      }
    } else {
      // Reset ao fechar
      setFormValues(null);
      setSelectedContactIds([]);
      setSelectedWhatsappIds([]);
      setAttachment(null);
      setAttachment2(null);
      setAttachment3(null);
      setActiveTab(0);
    }
  }, [open, maturationId]);

  const fetchWhatsapps = async () => {
    try {
      const { data } = await api.get("/whatsapp");
      setWhatsapps(data);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchContactPool = async () => {
    try {
      const { data } = await api.get("/maturation-contact-pools", {
        params: { pageNumber: 1, pageSize: 1000 }
      });
      setContactPool(data.records || []);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchMaturation = async () => {
    try {
      const { data } = await api.get(`/maturations/${maturationId}`);
      
      // Carregar contatos vinculados e definir IDs selecionados
      try {
        const contactsData = await api.get(`/maturations/${maturationId}/contacts`);
        const contactIds = (contactsData.data || []).map((c) => {
          // Tentar encontrar no pool pelo número
          const poolContact = contactPool.find((pc) => pc.number === c.number);
          return poolContact ? poolContact.id : null;
        }).filter((id) => id !== null);
        setSelectedContactIds(contactIds);
      } catch (err) {
        // Ignorar erro se não houver contatos ainda
        setSelectedContactIds([]);
      }
      
      // Carregar conexões WhatsApp selecionadas
      if (data.whatsappIds) {
        const whatsappIdsArray = typeof data.whatsappIds === 'string' 
          ? JSON.parse(data.whatsappIds) 
          : data.whatsappIds;
        setSelectedWhatsappIds(whatsappIdsArray || []);
      } else if (data.whatsappId) {
        setSelectedWhatsappIds([data.whatsappId]);
      }
      
      // Preparar dados para o formulário
      const formData = {
        ...data,
        scheduledAt: data.scheduledAt ? moment(data.scheduledAt).format("YYYY-MM-DDTHH:mm") : "",
        endAt: data.endAt ? moment(data.endAt).format("YYYY-MM-DDTHH:mm") : "",
        businessDays: data.businessDays ? (typeof data.businessDays === 'string' ? JSON.parse(data.businessDays) : data.businessDays) : initialValues.businessDays,
        // Garantir valores padrão para campos de ticket
        openTicket: data.openTicket || "disabled",
        statusTicket: data.statusTicket || "closed",
      };
      
      // Log para debug
      console.log("[MaturationModal] Dados carregados da maturação:", {
        mediaPath: data.mediaPath,
        mediaName: data.mediaName,
        mediaPath2: data.mediaPath2,
        mediaName2: data.mediaName2,
        mediaPath3: data.mediaPath3,
        mediaName3: data.mediaName3
      });
      
      setFormValues(formData);
      return formData;
    } catch (err) {
      toastError(err);
      setFormValues(null);
      return null;
    }
  };

  const handleDeleteMedia = async (mediaIndex) => {
    if (!maturationId) {
      // Se não tem ID, apenas limpar o estado local
      if (mediaIndex === 1) {
        setAttachment(null);
      } else if (mediaIndex === 2) {
        setAttachment2(null);
      } else if (mediaIndex === 3) {
        setAttachment3(null);
      }
      return;
    }

    try {
      setDeletingMedia(mediaIndex);
      await api.delete(`/maturations/${maturationId}/media-upload?mediaIndex=${mediaIndex}`);
      toast.success(`Mídia ${mediaIndex} removida com sucesso!`);
      
      // Recarregar dados da maturação para atualizar o formValues
      if (maturationId) {
        await fetchMaturation();
      }
      
      // Limpar estado local também
      if (mediaIndex === 1) {
        setAttachment(null);
      } else if (mediaIndex === 2) {
        setAttachment2(null);
      } else if (mediaIndex === 3) {
        setAttachment3(null);
      }
    } catch (err) {
      console.error(`[MaturationModal] Erro ao deletar mídia ${mediaIndex}:`, err);
      toastError(err);
    } finally {
      setDeletingMedia(null);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const dataValues = {
        ...values,
        scheduledAt: values.scheduledAt ? moment(values.scheduledAt).format("YYYY-MM-DD HH:mm:ss") : null,
        endAt: values.endAt ? moment(values.endAt).format("YYYY-MM-DD HH:mm:ss") : null,
        businessDays: JSON.stringify(values.businessDays),
        whatsappIds: JSON.stringify(selectedWhatsappIds),
        contactIds: selectedContactIds, // Array de IDs dos contatos selecionados
      };

      let result;
      if (maturationId) {
        result = await api.put(`/maturations/${maturationId}`, dataValues);
        // Atualizar contatos vinculados
        if (selectedContactIds.length > 0) {
          // Primeiro, remover todos os contatos existentes
          try {
            const existingContacts = await api.get(`/maturations/${maturationId}/contacts`);
            for (const contact of existingContacts.data || []) {
              await api.delete(`/maturations/${maturationId}/contacts/${contact.id}`);
            }
          } catch (err) {
            // Ignorar se não houver contatos
          }
          // Adicionar os novos contatos selecionados
          for (const contactId of selectedContactIds) {
            const contact = contactPool.find((c) => c.id === contactId);
            if (contact) {
              await api.post(`/maturations/${maturationId}/contacts`, {
                name: contact.name,
                number: contact.number,
                carrier: contact.carrier,
                notes: contact.notes,
              });
            }
          }
        }
      } else {
        result = await api.post("/maturations", dataValues);
        // Após criar, salvar contatos selecionados
        if (selectedContactIds.length > 0 && result.data.id) {
          for (const contactId of selectedContactIds) {
            const contact = contactPool.find((c) => c.id === contactId);
            if (contact) {
              await api.post(`/maturations/${result.data.id}/contacts`, {
                name: contact.name,
                number: contact.number,
                carrier: contact.carrier,
                notes: contact.notes,
              });
            }
          }
        }
      }

      // Upload de mídias - usar o ID da maturação criada/atualizada
      const finalMaturationId = result.data.id || maturationId;
      
      if (finalMaturationId) {
        try {
          if (attachment) {
            console.log("[MaturationModal] Fazendo upload de mídia 1:", attachment.name);
            const formData = new FormData();
            formData.append("file", attachment);
            const uploadResult1 = await api.post(`/maturations/${finalMaturationId}/media-upload?mediaIndex=1`, formData);
            console.log("[MaturationModal] Upload mídia 1 concluído:", uploadResult1.data);
          }
          if (attachment2) {
            console.log("[MaturationModal] Fazendo upload de mídia 2:", attachment2.name);
            const formData2 = new FormData();
            formData2.append("file", attachment2);
            const uploadResult2 = await api.post(`/maturations/${finalMaturationId}/media-upload?mediaIndex=2`, formData2);
            console.log("[MaturationModal] Upload mídia 2 concluído:", uploadResult2.data);
          }
          if (attachment3) {
            console.log("[MaturationModal] Fazendo upload de mídia 3:", attachment3.name);
            const formData3 = new FormData();
            formData3.append("file", attachment3);
            const uploadResult3 = await api.post(`/maturations/${finalMaturationId}/media-upload?mediaIndex=3`, formData3);
            console.log("[MaturationModal] Upload mídia 3 concluído:", uploadResult3.data);
          }
        } catch (uploadError) {
          console.error("[MaturationModal] Erro ao fazer upload de mídia:", uploadError);
          console.error("[MaturationModal] Detalhes do erro:", uploadError.response?.data || uploadError.message);
          toast.error(`Erro ao fazer upload de mídia: ${uploadError.response?.data?.message || uploadError.message}`);
        }
      } else {
        console.error("[MaturationModal] ID da maturação não disponível para upload de mídia");
        toast.warn("Maturação salva, mas não foi possível fazer upload das mídias (ID não encontrado).");
      }

      toast.success(maturationId ? "Maturação atualizada!" : "Maturação criada!");
      onSave();
      setSubmitting(false);
    } catch (err) {
      toastError(err);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className={classes.root} maxWidth="lg" fullWidth>
      <Formik
        initialValues={formValues || initialValues}
        validationSchema={MaturationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <Form>
            <DialogTitle>
              {maturationId ? "Editar Maturação" : "Nova Maturação"}
            </DialogTitle>
            <DialogContent dividers>
              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                <Tab label="Configurações" />
                <Tab label="Mensagens" />
              </Tabs>

              {/* ABA 1: Configurações */}
              {activeTab === 0 && (
                <Box className={classes.tabPanel}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Field
                        as={TextField}
                        name="name"
                        label="Nome da Maturação"
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        name="scheduledAt"
                        label="Data/Hora de Início (Opcional - para agendamento)"
                        type="datetime-local"
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        error={touched.scheduledAt && Boolean(errors.scheduledAt)}
                        helperText={touched.scheduledAt ? errors.scheduledAt : "Deixe vazio para iniciar manualmente"}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        name="endAt"
                        label="Data/Hora de Término (Opcional)"
                        type="datetime-local"
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        error={touched.endAt && Boolean(errors.endAt)}
                        helperText={touched.endAt ? errors.endAt : "Deixe vazio se não houver data de término"}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth variant="outlined" margin="dense">
                        <InputLabel>Conexões WhatsApp (selecione múltiplas)</InputLabel>
                        <Select
                          multiple
                          value={selectedWhatsappIds}
                          onChange={(e) => {
                            setSelectedWhatsappIds(e.target.value);
                            setFieldValue("whatsappIds", e.target.value);
                            // Se selecionar apenas uma, também definir whatsappId
                            if (e.target.value.length === 1) {
                              setFieldValue("whatsappId", e.target.value[0]);
                            }
                          }}
                          label="Conexões WhatsApp (selecione múltiplas)"
                          renderValue={(selected) => (
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {selected.map((id) => {
                                const w = whatsapps.find((w) => w.id === id);
                                return w ? <Chip key={id} label={w.name} size="small" /> : null;
                              })}
                            </Box>
                          )}
                        >
                          {whatsapps.map((w) => (
                            <MenuItem key={w.id} value={w.id}>
                              <Checkbox checked={selectedWhatsappIds.indexOf(w.id) > -1} />
                              {w.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth variant="outlined" margin="dense">
                        <InputLabel>Contatos (selecione do pool)</InputLabel>
                        <Select
                          multiple
                          value={selectedContactIds}
                          onChange={(e) => {
                            setSelectedContactIds(e.target.value);
                            setFieldValue("contactIds", e.target.value);
                          }}
                          label="Contatos (selecione do pool)"
                          renderValue={(selected) => (
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {selected.map((id) => {
                                const c = contactPool.find((c) => c.id === id);
                                return c ? (
                                  <Chip
                                    key={id}
                                    label={c.name || c.number}
                                    size="small"
                                  />
                                ) : null;
                              })}
                            </Box>
                          )}
                        >
                          {contactPool.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              <Checkbox checked={selectedContactIds.indexOf(c.id) > -1} />
                              {c.name || c.number} {c.carrier && `(${c.carrier})`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        name="messageInterval"
                        label="Intervalo entre Mensagens (segundos)"
                        type="number"
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        error={touched.messageInterval && Boolean(errors.messageInterval)}
                        helperText={touched.messageInterval && errors.messageInterval}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        name="businessStartTime"
                        label="Horário Início Comercial"
                        type="time"
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        error={touched.businessStartTime && Boolean(errors.businessStartTime)}
                        helperText={touched.businessStartTime && errors.businessStartTime}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        name="businessEndTime"
                        label="Horário Fim Comercial"
                        type="time"
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        error={touched.businessEndTime && Boolean(errors.businessEndTime)}
                        helperText={touched.businessEndTime && errors.businessEndTime}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      >
                        <InputLabel id="openTicket-selection-label">
                          Abrir Ticket
                        </InputLabel>
                        <Field
                          as={Select}
                          label="Abrir Ticket"
                          labelId="openTicket-selection-label"
                          id="openTicket"
                          name="openTicket"
                          error={touched.openTicket && Boolean(errors.openTicket)}
                        >
                          <MenuItem value="disabled">Desabilitado</MenuItem>
                          <MenuItem value="enabled">Habilitado</MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>

                    {values.openTicket === "enabled" && (
                      <Grid item xs={12} sm={6}>
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        >
                          <InputLabel id="statusTicket-selection-label">
                            Status do Ticket
                          </InputLabel>
                          <Field
                            as={Select}
                            label="Status do Ticket"
                            labelId="statusTicket-selection-label"
                            id="statusTicket"
                            name="statusTicket"
                            error={touched.statusTicket && Boolean(errors.statusTicket)}
                          >
                            <MenuItem value="open">Aberto</MenuItem>
                            <MenuItem value="pending">Pendente</MenuItem>
                            <MenuItem value="closed">Fechado</MenuItem>
                          </Field>
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* ABA 2: Mensagens */}
              {activeTab === 1 && (
                <Box className={classes.tabPanel}>
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4, 5].map((num) => (
                      <Grid item xs={12} key={num}>
                        <Field
                          as={TextField}
                          name={`message${num}`}
                          label={`Mensagem ${num}`}
                          fullWidth
                          multiline
                          rows={4}
                          variant="outlined"
                          margin="dense"
                          className={classes.messageField}
                          placeholder={`Digite a mensagem ${num}...`}
                        />
                      </Grid>
                    ))}

                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">
                        Variáveis disponíveis: {"{nome}"}, {"{telefone}"}
                      </Typography>
                    </Grid>

                    {/* Upload de mídias */}
                    <Grid item xs={12} sm={4}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <input
                          accept="image/*,video/*,.pdf"
                          style={{ display: "none" }}
                          id="media-1"
                          type="file"
                          onChange={(e) => setAttachment(e.target.files[0])}
                        />
                        <label htmlFor="media-1" style={{ flex: 1 }}>
                          <Button
                            variant="outlined"
                            component="span"
                            fullWidth
                            startIcon={<AttachFileIcon />}
                          >
                            Mídia 1 {attachment ? `(${attachment.name})` : formValues?.mediaName ? `(${formValues.mediaName})` : ""}
                          </Button>
                        </label>
                        {(attachment || formValues?.mediaName) && (
                          <Tooltip title="Remover mídia">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleDeleteMedia(1)}
                              disabled={deletingMedia === 1}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <input
                          accept="image/*,video/*,.pdf"
                          style={{ display: "none" }}
                          id="media-2"
                          type="file"
                          onChange={(e) => setAttachment2(e.target.files[0])}
                        />
                        <label htmlFor="media-2" style={{ flex: 1 }}>
                          <Button
                            variant="outlined"
                            component="span"
                            fullWidth
                            startIcon={<AttachFileIcon />}
                          >
                            Mídia 2 {attachment2 ? `(${attachment2.name})` : formValues?.mediaName2 ? `(${formValues.mediaName2})` : ""}
                          </Button>
                        </label>
                        {(attachment2 || formValues?.mediaName2) && (
                          <Tooltip title="Remover mídia">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleDeleteMedia(2)}
                              disabled={deletingMedia === 2}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <input
                          accept="image/*,video/*,.pdf"
                          style={{ display: "none" }}
                          id="media-3"
                          type="file"
                          onChange={(e) => setAttachment3(e.target.files[0])}
                        />
                        <label htmlFor="media-3" style={{ flex: 1 }}>
                          <Button
                            variant="outlined"
                            component="span"
                            fullWidth
                            startIcon={<AttachFileIcon />}
                          >
                            Mídia 3 {attachment3 ? `(${attachment3.name})` : formValues?.mediaName3 ? `(${formValues.mediaName3})` : ""}
                          </Button>
                        </label>
                        {(attachment3 || formValues?.mediaName3) && (
                          <Tooltip title="Remover mídia">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleDeleteMedia(3)}
                              disabled={deletingMedia === 3}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} color="secondary">
                Cancelar
              </Button>
              <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
                {isSubmitting ? "Salvando..." : "Salvar"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default MaturationModal;

